import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  AlertTitle,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TableChart as TableChartIcon,
  ShowChart as ShowChartIcon,
  Thermostat as ThermostatIcon,
  Cloud as CloudIcon,
  Opacity as OpacityIcon,
  LocationOn as LocationOnIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Assessment as ReportIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { poursAPI, DashboardResponse, Pour2Limit, type CalibrationCheck, type Pour } from '../../services/pours';
import ReportDialog from '../../components/ReportDialog';
import { MobilePourDashboard, type MobileTab } from '../../components/dashboard/mobile/MobilePourDashboard';
import type { MobileChartKey } from '../../components/dashboard/mobile/mobileDashboardModel';
import {
  DEFAULT_SCALE_MAX,
  type TempLimitMarker,
} from '../../components/dashboard/TemperatureProfileBar';
import { PourHeader } from '../../components/dashboard/PourHeader';
import { LocationList } from '../../components/dashboard/LocationList';
import { LocationDetails } from '../../components/dashboard/LocationDetails';
import { MultiLocationChart } from '../../components/dashboard/MultiLocationChart';
import { WeatherStationChart } from '../../components/dashboard/WeatherStationChart';
import { SiteWeatherCard } from '../../components/dashboard/SiteWeather';
import { linkedSiteWeather } from '../../utils/siteConditions';
import { usePourNav } from '../../context/PourNavContext';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/userPermissions';
import { isAmbientChannel, isSiteConditionsChannel } from '../../utils/locationChannels';
import { maturityMeta, maturityShortLabel, pourHasCurve } from '../../utils/maturityDisplay';
import { pourComputesMaturity } from '../../utils/calibrationCheck';
import { apiErrorText } from '../../utils/apiError';
import { useIsMobile } from '../../utils/useIsMobile';
import { readDashboardSummary, writeDashboardSummary } from '../../utils/dashboardSummaryCache';
import { pastPlannedEnd, plannedEndSentence } from '../../utils/pastPlannedEnd';

/** The page polls the dashboard (and the open charts) this often, while
 *  the page is visible. */
const DASHBOARD_POLL_MS = 5 * 60 * 1000;

/** Phones paint the last reply for a pour at once while the fresh one
 *  loads, when it is no older than this. */
const SUMMARY_SEED_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** Map a pour's temperature limits (Criteria '02') to bar markers. */
const tempMarkersFromLimits = (limits: Pour2Limit[]): TempLimitMarker[] =>
  limits
    .filter((l) => l.Criteria === '02')
    .map((l) => ({
      temp: Number(l.LimitsTemp),
      color: l.BackgroundColor || undefined,
      label: l.Purpose || undefined,
    }));

/** Bar scale max from the group's "Temperature Bar Indicator" row (Criteria
 *  '03'); falls back to the default (110) when none is configured. */
const scaleMaxFromLimits = (limits: Pour2Limit[]): number => {
  const bar = limits.find((l) => l.Criteria === '03');
  const v = bar ? Number(bar.LimitsTemp) : NaN;
  return v > 0 ? v : DEFAULT_SCALE_MAX;
};

const SIDEBAR_WIDTH = 200;

/** ASTM C1074 9.2 banner per calibration check status. A match, or a pour
 *  without a calibration, needs no banner. A mismatch pour shows no strength
 *  anywhere, and the server's text for it says so; a pour whose inputs only
 *  differ keeps its strength, with the warning. */
const CALIBRATION_BANNER_SEVERITY: Partial<
  Record<CalibrationCheck['status'], 'error' | 'warning' | 'info'>
> = {
  mismatch: 'error',
  differs: 'warning',
  unknown: 'info',
};

interface NavItem {
  key: string;
  icon: React.ReactElement;
  labelKey: string;
  fallback: string;
  short: string;
  /** Values the translated label interpolates (e.g. the EA section's name). */
  labelValues?: Record<string, string>;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', icon: <TableChartIcon fontSize="small" />, labelKey: 'pageTitles.pourDashboard', fallback: 'Pour Dashboard', short: 'Dashboard' },
  { key: 'location', icon: <LocationOnIcon fontSize="small" />, labelKey: 'pourDashboard.locationDetails', fallback: 'Location Details', short: 'Location' },
  { key: 'strength', icon: <ShowChartIcon fontSize="small" />, labelKey: 'pourDashboard.strengthCharts', fallback: 'Strength Charts', short: 'Strength' },
  { key: 'temperature', icon: <ThermostatIcon fontSize="small" />, labelKey: 'pourDashboard.temperatureCharts', fallback: 'Temperature Data', short: 'Temp' },
  { key: 'humidity', icon: <OpacityIcon fontSize="small" />, labelKey: 'pourDashboard.tempHumidityCharts', fallback: 'Temp & Humidity Data', short: 'Temp & RH' },
  { key: 'weather', icon: <CloudIcon fontSize="small" />, labelKey: 'pourDashboard.weatherData', fallback: 'Weather Data', short: 'Weather' },
];

export const PourDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { pourId } = useParams<{ pourId: string }>();
  const navigate = useNavigate();
  // Deep-link target from the Control Panel: tapping a location row navigates
  // here with the location id (and a request to open the Location Details tab).
  const routerLocation = useLocation();
  // From the phone Control Panel the state also carries the list row
  // (listPour), so the phone header and status strip paint at once.
  const navState = routerLocation.state as { locationId?: string; view?: string; listPour?: Pour } | null;
  const theme = useTheme();
  // Phones and tablets (below md) get the phone dashboard, an early return
  // below; noSsr, so a phone never mounts the desktop body first.
  const isMobile = useIsMobile();
  // Below sm the section nav moves into the global bottom bar; between sm and md
  // (tablet) it stays as a top chip strip since the bottom bar isn't shown there.
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { setNav, setTitle } = usePourNav();
  // Resetting a location's concrete start edits the pour: the API needs
  // pours.create, the same permission PourSetup uses for editing.
  const { user } = useAuth();
  const canEditPours = hasPermission(user, 'pours.create');
  const userId = user?.UserID ?? null;
  // Phones start from the last reply for this pour (in memory, this user,
  // at most 2 h old) while the fresh one loads; desktop starts empty.
  const [seed] = useState(() => {
    if (!isMobile || !pourId) return null;
    const entry = readDashboardSummary(userId, pourId);
    return entry && Date.now() - entry.fetchedAt <= SUMMARY_SEED_MAX_AGE_MS ? entry : null;
  });
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(seed?.data ?? null);
  const [pourLimits, setPourLimits] = useState<Pour2Limit[]>(seed?.limits ?? []);
  const pourLimitsRef = useRef(pourLimits);
  pourLimitsRef.current = pourLimits;
  const [loading, setLoading] = useState(!seed);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(() => (seed ? new Date(seed.fetchedAt) : new Date()));
  // A refresh in flight (the header's spinning icon); the first load uses
  // `loading` instead.
  const [refreshing, setRefreshing] = useState(false);
  // When a background refresh last failed while data was on screen: the page
  // keeps that data and the header says it could not refresh.
  const [refreshFailedAt, setRefreshFailedAt] = useState<Date | null>(null);
  // Bumped after each successful poll / manual refresh so Location Details
  // and the overlay charts reload their readings quietly too.
  const [refreshTick, setRefreshTick] = useState(0);
  const hasDataRef = useRef(!!seed);
  // 'location' is always index 1 in NAV_ITEMS (only strength/weather get filtered).
  const [activeView, setActiveView] = useState(navState?.view === 'location' ? 1 : 0);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(navState?.locationId);
  // The phone dashboard's tab and its Charts pick, kept here so they
  // survive a tab switch; the Control Panel deep link opens Location.
  const [mobileTab, setMobileTab] = useState<MobileTab>(navState?.view === 'location' ? 'location' : 'overview');
  const [mobileChartKey, setMobileChartKey] = useState<MobileChartKey | null>(null);

  const handleSelectLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
    setActiveView(1);
    setMobileTab('location');
  };

  // Split a multi-function pour's locations between the chart sections:
  // concrete channels feed Temperature Data; ambient Temp & Humidity hardware
  // (incl. its humidity channels) feeds Temp & Humidity Data; weather
  // stations belong to neither (their data lives in the weather pipeline and
  // the Weather Data section). Memoised per dashboard response;
  // MultiLocationChart keys its loads on the location IDs, so a poll's new
  // arrays reload nothing on their own (the refresh signal does, quietly).
  const { concreteLocations, ambientLocations } = useMemo(() => {
    const locs = dashboardData?.locations ?? [];
    return {
      concreteLocations: locs.filter((l) => !isSiteConditionsChannel(l)),
      ambientLocations: locs.filter(isAmbientChannel),
    };
  }, [dashboardData]);

  // Only the sections this pour's functions need: Dashboard / Location always;
  // Temperature for concrete channels; Temp & Humidity for ambient channels;
  // Strength for maturity pours; Weather for mass-concrete or evaporation
  // pours (mirrors the graph gating in LocationDetails).
  const visibleNavItems = useMemo(() => {
    const pour = dashboardData?.pour;
    const fns = (pour?.monitoring_functions ?? '')
      .split(',').map((s) => s.trim()).filter(Boolean);
    const hasFns = fns.length > 0;
    const func = pour?.functionality;
    const showStrength = hasFns
      ? fns.includes('Concrete Maturity')
      : func === '1' || func === '3';
    const showEvaporation = hasFns ? fns.includes('Calculate Concrete Evaporation Rate') : false;
    // Weather needs an actual weather function — External Temperature and
    // Humidity is ambient-sensor data, not weather-station data, so it must
    // NOT surface the Weather tab. Legacy pours without a functions string
    // fall back to the coarse functionality code.
    const showWeather = hasFns
      ? fns.includes('Onsite Weather Conditions') || showEvaporation
      : func === '2' || func === '3';
    // Without a usable calibration curve the "strength" section plots the
    // raw maturity index, so name it for what it shows (TTF for Nurse Saul,
    // EA at the specified temperature for every other method) instead of
    // promising strength.
    const maturity = maturityMeta(pour);
    const maturityShort = maturityShortLabel(maturity);
    // Multi-function pours get one chart section per function present:
    // Temperature Data for concrete channels, Temp & Humidity Data for
    // ambient hardware, Weather Data via the function gating above. A pour
    // with only ambient hardware hides Temperature Data; legacy/empty pours
    // keep it. A weather station is site weather, never a location: a pour
    // that records only site weather has no location sections at all. A
    // station that is not linked shows nothing, so it does not count.
    const allLocations = dashboardData?.locations ?? [];
    const weatherOnly = allLocations.length === 0 && !!linkedSiteWeather(dashboardData?.site_weather);
    const siteOnly = (allLocations.length > 0 && concreteLocations.length === 0) || weatherOnly;
    return NAV_ITEMS.filter((item) => {
      if (item.key === 'strength') return showStrength && !weatherOnly;
      if (item.key === 'weather') return showWeather;
      if (item.key === 'temperature') return !siteOnly;
      if (item.key === 'location') return !weatherOnly;
      if (item.key === 'humidity') return ambientLocations.length > 0;
      return true;
    }).map((item) => {
      if (item.key === 'strength' && pour && !pourHasCurve(pour)) {
        return {
          ...item,
          fallback: `${maturityShort} Charts`,
          short: maturityShort,
          labelKey: maturity.kind === 'ea' ? 'pourDashboard.eaCharts' : 'pourDashboard.ttfCharts',
          labelValues: { maturity: maturityShort },
        };
      }
      return item;
    });
  }, [dashboardData, concreteLocations, ambientLocations]);

  // Keep the active index in range if the visible set shrinks.
  useEffect(() => {
    if (visibleNavItems.length > 0 && activeView >= visibleNavItems.length) {
      setActiveView(0);
    }
  }, [visibleNavItems, activeView]);

  // Publish the section tabs to the mobile bottom bar (and keep the active index
  // in sync); clear them when leaving the dashboard. The phone dashboard
  // publishes its own three tabs instead.
  useEffect(() => {
    if (isMobile) return undefined;
    setNav({
      items: visibleNavItems.map((n) => ({ key: n.key, label: n.short, icon: n.icon })),
      active: activeView,
      onSelect: setActiveView,
    });
    return () => setNav(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, visibleNavItems, isMobile]);

  // Publish "Project > Pour" to the app header (the space above the return link).
  useEffect(() => {
    const pour = dashboardData?.pour;
    if (!pour) return;
    setTitle({
      project: pour.project_name || undefined,
      pour: pour.name || pour.description || undefined,
    });
    return () => setTitle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData?.pour?.project_name, dashboardData?.pour?.name, dashboardData?.pour?.description]);

  /** Load the dashboard summary and limits. With data on screen a failure
   *  keeps it, and the header says it could not refresh; only a first load
   *  that fails shows the error screen. `charts` also has Location Details
   *  and the overlay charts reload their readings, quietly. Loads are
   *  numbered so an older response never replaces a newer one. */
  const fetchSeq = useRef(0);
  const fetchDashboardData = useCallback(async ({ charts = false }: { charts?: boolean } = {}) => {
    if (!pourId) {
      setError('Pour ID is required');
      setLoading(false);
      return;
    }

    const seq = ++fetchSeq.current;
    setRefreshing(true);
    try {
      const [data, limits] = await Promise.all([
        // Phones also ask for each location's limit state (site_state);
        // desktop's request is unchanged.
        isMobile ? poursAPI.getDashboardData(pourId, { siteState: true }) : poursAPI.getDashboardData(pourId),
        // A failed limits request keeps the limits already on screen (none
        // before the first load), so the limit markers and lines never
        // vanish until the next poll.
        poursAPI.getPourLimits(pourId).catch((): Pour2Limit[] | null => null),
      ]);
      if (seq !== fetchSeq.current) return;
      setDashboardData(data);
      if (limits) setPourLimits(limits);
      setLastRefresh(new Date());
      setRefreshFailedAt(null);
      setError(null);
      hasDataRef.current = true;
      if (charts) setRefreshTick((tick) => tick + 1);
      // The next visit on this phone paints this reply at once.
      if (isMobile) {
        writeDashboardSummary(userId, pourId, { data, limits: limits ?? pourLimitsRef.current, fetchedAt: Date.now() });
      }
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching dashboard data:', err);
      if (hasDataRef.current) {
        setRefreshFailedAt(new Date());
      } else {
        setError(apiErrorText(err, 'Failed to fetch dashboard data'));
      }
    } finally {
      if (seq === fetchSeq.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  }, [pourId, isMobile, userId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // The 5-minute poll refreshes the summary and the open section's charts,
  // only while the page is visible (as the control panel): a hidden tab
  // makes no requests, and one that comes back after a missed poll
  // refreshes once, straight away.
  const lastPollRef = useRef(Date.now());
  useEffect(() => {
    const visible = () => document.visibilityState !== 'hidden';
    const poll = () => {
      lastPollRef.current = Date.now();
      fetchDashboardData({ charts: true });
    };
    const interval = setInterval(() => {
      if (visible()) poll();
    }, DASHBOARD_POLL_MS);
    const onVisibilityChange = () => {
      if (visible() && Date.now() - lastPollRef.current >= DASHBOARD_POLL_MS) poll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchDashboardData]);

  const handleManualRefresh = () => {
    fetchDashboardData({ charts: true });
  };

  // After a concrete start reset Location Details reloads its own chart;
  // the page reloads the summary (maturity, strength, targets).
  const handleMaturityChanged = () => {
    fetchDashboardData();
  };

  // The error screen's retry: back to the loading screen meanwhile.
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchDashboardData();
  };

  // Report generation dialog (type + options) and its success/error feedback.
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportTemperature = useCallback(async () => {
    if (!pourId) return;
    setExporting(true);
    try {
      await poursAPI.exportTemperatureData(pourId);
    } catch {
      setExportError(t('pourDashboard.exportFailed', 'Failed to export temperature data'));
    } finally {
      setExporting(false);
    }
  }, [pourId, t]);

  const handleExportStrength = useCallback(async () => {
    if (!pourId) return;
    setExporting(true);
    try {
      await poursAPI.exportStrengthData(pourId);
    } catch {
      setExportError(t('pourDashboard.exportStrengthFailed', 'Failed to export strength data'));
    } finally {
      setExporting(false);
    }
  }, [pourId, t]);

  // Phones and tablets: the phone dashboard (its own header, three tabs,
  // cards and charts), also while the first load runs. A first load that
  // fails shows the error screen below.
  if (isMobile && !error && (dashboardData || loading)) {
    return (
      <MobilePourDashboard
        pourId={pourId || ''}
        data={dashboardData}
        listPour={navState?.listPour ?? null}
        pourLimits={pourLimits}
        lastRefresh={lastRefresh}
        refreshing={refreshing}
        refreshFailedAt={refreshFailedAt}
        onRefresh={handleManualRefresh}
        refreshTick={refreshTick}
        tab={mobileTab}
        onTabChange={setMobileTab}
        chartKey={mobileChartKey}
        onChartKeyChange={setMobileChartKey}
        selectedLocationId={selectedLocationId}
        onSelectLocation={handleSelectLocation}
        onStopped={handleManualRefresh}
      />
    );
  }

  if (loading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 1.5, color: 'text.secondary' }}>{t('pourDashboard.loadingDashboard')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: '60rem', mx: 'auto', px: 4, py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          {t('pourDashboard.backToControlPanel')}
        </Button>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ color: 'error.main', mb: 2 }}>
            <svg style={{ width: 48, height: 48, margin: '0 auto', display: 'block' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>{t('pourDashboard.errorLoading')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{error}</Typography>
          <Button variant="contained" onClick={handleRetry}>{t('common.tryAgain')}</Button>
        </Paper>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ maxWidth: '60rem', mx: 'auto', px: 4, py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{t('pourDashboard.noData')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{t('pourDashboard.noDataDescription')}</Typography>
        </Paper>
      </Box>
    );
  }

  // The pour against its calibration (ASTM C1074 9.2), from the stored pour.
  // Only for a pour that computes maturity: one that shows no maturity or
  // strength (e.g. Temperature Only with a legacy CalibrationID) needs no banner.
  const calibrationCheck = dashboardData.pour.calibration_check;
  const calibrationBannerSeverity =
    calibrationCheck?.text &&
    pourComputesMaturity(dashboardData.pour.monitoring_functions, dashboardData.pour.functionality)
      ? CALIBRATION_BANNER_SEVERITY[calibrationCheck.status]
      : undefined;

  // A running pour past the planned end of its own stop setting, by the
  // Control Panel row's rule (utils/pastPlannedEnd), so a person sees it and
  // stops it by hand. Display only.
  const bannerNow = Date.now();
  const pastEnd = pastPlannedEnd(
    {
      running: (dashboardData.pour.status ?? '').trim() === '1' && !dashboardData.pour.scheduled,
      stopType: dashboardData.pour.stop_type,
      xHours: dashboardData.pour.x_hours,
      presetStopTime: dashboardData.pour.preset_stop_time,
      started: dashboardData.pour.started_at,
      loggerStart: dashboardData.pour.logger_start,
    },
    bannerNow,
  );

  const renderContent = () => {
    switch (visibleNavItems[activeView]?.key) {
      case 'dashboard': {
        // The pour's site weather (its weather station) once, above its
        // monitoring locations; the station is never one of them. Only a
        // linked station shows.
        const site = linkedSiteWeather(dashboardData.site_weather);
        const weatherIndex = visibleNavItems.findIndex((item) => item.key === 'weather');
        return (
          <>
            {site && (
              <SiteWeatherCard
                site={site}
                pourId={pourId}
                refreshSignal={refreshTick}
                pourStatus={dashboardData.pour.status}
                windSpeedUnit={dashboardData.pour.wind_speed_unit}
                timezoneIana={dashboardData.pour.timezone_iana}
                onOpenWeatherData={weatherIndex >= 0 ? () => setActiveView(weatherIndex) : undefined}
              />
            )}
            {(dashboardData.locations.length > 0 || !site) && (
              <LocationList
                locations={dashboardData.locations}
                pour={dashboardData.pour}
                onSelectLocation={handleSelectLocation}
                limits={tempMarkersFromLimits(pourLimits)}
                scaleMax={scaleMaxFromLimits(pourLimits)}
              />
            )}
          </>
        );
      }
      case 'location':
        return (
          <LocationDetails
            locations={dashboardData.locations}
            pourId={pourId || ''}
            pourFunctionality={dashboardData.pour.functionality}
            monitoringFunctions={dashboardData.pour.monitoring_functions}
            maturityMethod={dashboardData.pour.maturity_method}
            maturity={dashboardData.pour.maturity}
            initialLocationId={selectedLocationId}
            timezoneIana={dashboardData.pour.timezone_iana}
            pourLimits={pourLimits}
            strengthUnit={dashboardData.pour.strength_unit}
            onMaturityChanged={handleMaturityChanged}
            // A locked pour (e.g. finished) is never recalculated until an
            // admin unlocks it.
            canResetConcreteStart={canEditPours && !dashboardData.pour.locked}
            // Each poll / refresh reloads the selected location quietly.
            refreshSignal={refreshTick}
            pourStatus={dashboardData.pour.status}
            // A 9.2 mismatch pour shows no strength; the Targets card says why.
            calibrationCheckStatus={dashboardData.pour.calibration_check?.status}
          />
        );
      case 'strength':
        // Strength (calibrated pours only) + the raw maturity index chart —
        // legacy keeps both as separate pour-level charts. One component so
        // the per-location data is fetched once and shared by both panels.
        // Excel export mirrors the Temperature sections: same readings,
        // trimmed to the strength / maturity columns.
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                onClick={handleExportStrength}
                disabled={exporting}
              >
                {t('pourDashboard.exportExcel', 'Export to Excel')}
              </Button>
            </Box>
            <MultiLocationChart
              pourId={pourId || ''}
              locations={dashboardData.locations}
              metrics={pourHasCurve(dashboardData.pour) ? ['strength', 'maturity'] : ['maturity']}
              maturityMethod={dashboardData.pour.maturity_method}
              maturity={dashboardData.pour.maturity}
              strengthUnit={dashboardData.pour.strength_unit}
              timezoneIana={dashboardData.pour.timezone_iana}
              refreshSignal={refreshTick}
            />
          </Box>
        );
      case 'temperature':
      case 'humidity': {
        // Dual-purpose pours split the overlay chart per function: concrete
        // channels on Temperature Data, ambient hardware (its temp + humidity
        // channels) on Temp & Humidity Data. The Excel export is pour-wide,
        // so both sections offer it.
        const isHumiditySection = visibleNavItems[activeView]?.key === 'humidity';
        const sectionLocations = isHumiditySection
          ? ambientLocations
          : concreteLocations.length > 0
            ? concreteLocations
            : dashboardData.locations;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                onClick={handleExportTemperature}
                disabled={exporting}
              >
                {t('pourDashboard.exportExcel', 'Export to Excel')}
              </Button>
            </Box>
            <MultiLocationChart
              pourId={pourId || ''}
              locations={sectionLocations}
              metrics={['temperature']}
              timezoneIana={dashboardData.pour.timezone_iana}
              refreshSignal={refreshTick}
            />
          </Box>
        );
      }
      case 'weather': {
        // The station's own readings (air temperature, humidity, wind), then
        // the evaporation rate: only on a pour that calculates it, and only
        // for concrete locations (site-conditions hardware has none).
        const calculatesEvaporation = (dashboardData.pour.monitoring_functions ?? '')
          .split(',').map((f) => f.trim()).includes('Calculate Concrete Evaporation Rate');
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <WeatherStationChart
              pourId={pourId || ''}
              timezoneIana={dashboardData.pour.timezone_iana}
              refreshSignal={refreshTick}
              windSpeedUnit={dashboardData.pour.wind_speed_unit}
              pourStartAt={dashboardData.pour.logger_start ?? dashboardData.pour.started_at}
            />
            {calculatesEvaporation && concreteLocations.length > 0 && (
              <MultiLocationChart
                pourId={pourId || ''}
                locations={concreteLocations}
                metrics={['weather']}
                timezoneIana={dashboardData.pour.timezone_iana}
                refreshSignal={refreshTick}
              />
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  // Return link + pour actions. On desktop this sits inside the content column,
  // level with the top of the section sidebar (Figma); on mobile it spans the page.
  const headerRow = (
    <Box
      sx={{
        px: 2,
        // 52px = the sidebar list's 8px top padding + its 44px first item, so the
        // arrow and the "Pour Dashboard" label share a centre line. No divider:
        // the table's own top edge separates the row from the content.
        height: 52,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        minWidth: 0,
        overflow: 'hidden',
        gap: 1,
      }}
    >
      {/* The pour title lives in the app header; this row is the back arrow plus actions. */}
      <Tooltip title={t('pourDashboard.backToControlPanel')}>
        <IconButton size="small" onClick={() => navigate('/')} sx={{ color: 'text.secondary', flexShrink: 0 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box sx={{ flex: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={t('pourDashboard.viewPour', 'View Pour')}>
          <IconButton size="small" onClick={() => navigate(`/pours/${pourId}`)} sx={{ color: 'text.secondary' }}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('pourDashboard.editPour', 'Edit Pour')}>
          <IconButton size="small" onClick={() => navigate(`/pours/${pourId}/edit`)} sx={{ color: 'text.secondary' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('pourDashboard.generateReport', 'Generate Report')}>
          <IconButton
            size="small"
            onClick={() => setReportDialogOpen(true)}
            sx={{ color: 'text.secondary' }}
          >
            <ReportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <PourHeader
          pour={dashboardData.pour}
          weather={dashboardData.weather}
          onRefresh={handleManualRefresh}
          lastRefresh={lastRefresh}
          isRefreshing={refreshing}
          refreshFailedAt={refreshFailedAt}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      // Desktop: a fixed-height app shell with an internally-scrolling content
      // area. Mobile: a normal page that flows and scrolls once (no nested
      // scroll containers, so it doesn't fight the page/bottom-nav scroll).
      height: { xs: 'auto', md: 'calc(100vh - 80px)' },
      mx: -3,
      mt: -1.5,
      mb: { xs: 0, md: -3 },
      overflow: { xs: 'visible', md: 'hidden' },
    }}>
      {/* Phones/tablets: the return link row spans the full width above the nav strip. */}
      {isMobile && headerRow}

      {/* Main body: sidebar + content. Navigation lives in the desktop sidebar,
          a tablet top-strip, or (on phones) the global bottom bar. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflow: { xs: 'visible', md: 'hidden' } }}>
        {/* Navigation — phones use the bottom bar (rendered in the layout) */}
        {isXs ? null : isMobile ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              px: 1.5,
              py: 1,
              flexShrink: 0,
              overflowX: 'auto',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {visibleNavItems.map((item, index) => (
              <Chip
                key={item.key}
                label={item.short}
                onClick={() => setActiveView(index)}
                size="small"
                sx={{
                  flexShrink: 0,
                  borderRadius: '999px',
                  fontWeight: 500,
                  height: 30,
                  border: '1px solid',
                  borderColor: activeView === index ? 'primary.main' : 'grey.300',
                  bgcolor: activeView === index ? 'primary.main' : 'grey.100',
                  color: activeView === index ? 'primary.contrastText' : 'text.secondary',
                  '&:hover': { bgcolor: activeView === index ? 'primary.dark' : 'grey.200' },
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              bgcolor: '#f7f7f7',
              overflow: 'auto',
            }}
          >
            <List disablePadding sx={{ px: 1, pt: 1 }}>
              {visibleNavItems.map((item, index) => (
                <ListItemButton
                  key={item.key}
                  selected={activeView === index}
                  onClick={() => setActiveView(index)}
                  sx={{
                    minHeight: 44,
                    py: 1,
                    px: 1.5,
                    mb: 0.25,
                    borderRadius: '6px',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.05)',
                      color: 'text.primary',
                    },
                    // Selected: the same brand-green tint used for the active
                    // icon in the main navigation rail.
                    '&.Mui-selected': {
                      bgcolor: 'rgba(121,168,42,0.14)',
                      color: 'text.primary',
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      borderRadius: '0 6px 6px 0',
                      pl: 1.25,
                      '&:hover': { bgcolor: 'rgba(121,168,42,0.22)' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey, { defaultValue: item.fallback, ...item.labelValues })}
                    primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: activeView === index ? 600 : 400, noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        {/* Content column: return-link row, then the area that scrolls internally on desktop / flows on mobile */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>
          {!isMobile && headerRow}
          {/* Desktop: no top padding, the arrow row above already provides the spacing. */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', md: 'auto' }, p: { xs: 1.5, md: 2 }, pt: { md: 0 } }}>
          {calibrationCheck && calibrationBannerSeverity && (
            <Alert
              severity={calibrationBannerSeverity}
              data-testid="calibration-check-banner"
              sx={{ mb: 1.5 }}
            >
              {calibrationCheck.text}
            </Alert>
          )}
          {pastEnd && (
            <Alert severity="warning" data-testid="past-planned-end-banner" sx={{ mb: 1.5 }}>
              <AlertTitle>{t('pourDashboard.pastPlannedEndTitle', 'Past its planned end')}</AlertTitle>
              {plannedEndSentence(pastEnd, bannerNow, dashboardData.pour.timezone_iana, t)}{' '}
              {t(
                'pourDashboard.pastPlannedEndAction',
                'It is still In Progress. If monitoring has finished, stop it from the Control Panel.',
              )}
            </Alert>
          )}
          {renderContent()}

          {dashboardData.attachments && dashboardData.attachments.length > 0 && activeView === 0 && (
            <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
              <Box sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 2 }}>{t('pourDashboard.attachments')}</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dashboardData.attachments.map((attachment, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <svg style={{ width: 20, height: 20, color: '#9e9e9e' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {attachment.name}
                    </a>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={!!reportMessage}
        autoHideDuration={6000}
        onClose={() => setReportMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setReportMessage(null)}>
          {reportMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      </Snackbar>
      <ReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        pourId={pourId || ''}
        pourCode={dashboardData?.pour?.name}
        onSuccess={(msg) => setReportMessage(msg)}
      />
    </Box>
  );
};
